import { FC, useState, useEffect } from "react";
import { Box, Modal, Stack, styled, Typography, Button as ButtonMui, Divider, Grid } from "@mui/material";
import { IconChevronLeft, IconChevronRight } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { EMPTY_STR } from "@/constants/string";
import { TTalentUploadFile } from "@/features/talent/types/talentForm";
import { useSharedFileSignedUrl, fetchSharedSignedUrlApi } from "@/api/services/main/sharing";
import { background, divider, text } from "@/theme/colors";
import { textEllipsis } from "@/theme/minxin";
import { dayjs } from "@/utils/dayjs";
import { DATE_TIME_FORMAT_WITH_SECONDS } from "@/constants/datetime";
import { showError } from "@/utils/error";
import { downloadFileByUrl } from "@/utils/export";
import { TIFFPreview } from "@/components/commons/TIffPreview";
import { toNumber } from "lodash";
import { convertByteToKb, convertByteToMb } from "@/utils/file";

type TSharedFilePreviewModalProps = {
  open: boolean;
  onClose: () => void;
  files: TTalentUploadFile[];
  currentFileIndex: number;
  shareUrl: string;
};

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];
const TIFF_EXTENSIONS = ["tif", "tiff"];

const getExtension = (path: string) => path.split(".").pop()?.toLowerCase() ?? "";

/** Extract display name from filePath, removing timestamp prefix */
const getFileName = (filePath: string) => {
  const segments = filePath.split("/");
  const fullName = segments[segments.length - 1] || filePath;
  const underscoreIdx = fullName.indexOf("_");
  return underscoreIdx > 0 ? fullName.substring(underscoreIdx + 1) : fullName;
};

/** File preview modal for shared (no-auth) view — uses shared signed URL API */
export const SharedFilePreviewModal: FC<TSharedFilePreviewModalProps> = ({ open, onClose, files, currentFileIndex, shareUrl }) => {
  const [currentIndex, setCurrentIndex] = useState(currentFileIndex);

  const currentFile = files[currentIndex];
  const currentFilePath = currentFile?.filePath;
  const { data: signedUrlData } = useSharedFileSignedUrl(currentFilePath, shareUrl);
  const signedUrl = signedUrlData?.data?.signedUrl;

  const ext = getExtension(currentFilePath ?? "");
  const isImage = IMAGE_EXTENSIONS.includes(ext);
  const isTiff = TIFF_EXTENSIONS.includes(ext);
  const fileName = currentFilePath ? getFileName(currentFilePath) : EMPTY_STR.TEXT;

  const [fileSize, setFileSize] = useState<number | null>(null);

  const canGoNext = currentIndex < files.length - 1;
  const canGoPrev = currentIndex > 0;

  useEffect(() => {
    setCurrentIndex(currentFileIndex);
  }, [currentFileIndex, open]);

  // Fetch file size via blob (file is already loaded for preview)
  useEffect(() => {
    setFileSize(null);
    if (!signedUrl) return;
    fetch(signedUrl)
      .then((res) => res.blob())
      .then((blob) => setFileSize(blob.size))
      .catch(() => {
        // Ignore — file size is optional
      });
  }, [signedUrl]);

  const handleDownload = async () => {
    if (!currentFilePath) return;
    try {
      const res = await fetchSharedSignedUrlApi(currentFilePath, shareUrl);
      await downloadFileByUrl(fileName, res.data.signedUrl);
    } catch (error) {
      showError(error);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer>
        <Stack>
          <Stack gap={2}>
            <FileName variant="sub16Bold" color={text.secondary}>
              {fileName}
            </FileName>
            {isImage ? (
              signedUrl ? (
                <Box component="img" src={signedUrl} sx={{ width: 800, height: 500, objectFit: "contain" }} alt={fileName} />
              ) : null
            ) : isTiff ? (
              <TIFFPreview url={signedUrl} width={800} height={500} />
            ) : (
              <iframe src={signedUrl} style={{ marginBottom: "20px", height: 500, width: 800, border: "none" }} title="PDF Page" allowFullScreen />
            )}
          </Stack>
          <Stack pt={2} gap={0.5}>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Typography variant="body14" color={text.secondary}>
                  更新日時
                </Typography>
                <Divider />
                <Typography variant="body14">
                  {currentFile?.updatedAt ? dayjs(currentFile.updatedAt).format(DATE_TIME_FORMAT_WITH_SECONDS) : EMPTY_STR.DATE_TIME}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body14" color={text.secondary}>
                  ファイル容量
                </Typography>
                <Divider />
                <Typography variant="body14">
                  {fileSize !== null
                    ? fileSize >= 1000 * 1000
                      ? `${convertByteToMb(fileSize)}MB`
                      : `${convertByteToKb(fileSize)}KB`
                    : EMPTY_STR.TEXT}
                </Typography>
              </Grid>
            </Grid>
          </Stack>
          <Stack py={2} flexDirection="row" gap={2} justifyContent="space-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setCurrentIndex(0);
                onClose();
              }}
            >
              キャンセル
            </Button>
            <Button variant="primary" size="sm" onClick={handleDownload}>
              ダウンロード
            </Button>
          </Stack>
          {toNumber(files.length) > 1 && (
            <Stack pt={2} flexDirection="row" gap={2} justifyContent="center" alignItems="center">
              <BoxIcon onClick={() => canGoPrev && setCurrentIndex((i) => i - 1)} disabled={!canGoPrev}>
                <IconChevronLeft width={20} height={20} color={text.primary} />
              </BoxIcon>
              <BoxIcon onClick={() => canGoNext && setCurrentIndex((i) => i + 1)} disabled={!canGoNext}>
                <IconChevronRight width={20} height={20} color={text.primary} />
              </BoxIcon>
            </Stack>
          )}
        </Stack>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-width: 348px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  padding: 24px;
  max-height: 90vh;
`;

const FileName = styled(Typography)`
  max-width: 300px;
  ${textEllipsis};
  flex: 1;
`;

const BoxIcon = styled(ButtonMui)`
  padding: 0;
  min-width: 24px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${divider.middle};
  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;
